import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  IconButton,
  Alert
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import type { ProcedureType } from '../../../../../services/EncounterService/types/encounterTypes';
import { useServiceForm } from './hooks';
import {
  BasicInfoSection,
  ServiceInfoSection,
  TimingInfoSection,
  RoomInfoSection,
  DialogActionButtons
} from './components';

interface ServiceEditDialogProps {
  procedure: ProcedureType | null;
  open: boolean;
  onClose: () => void;
  onServiceUpdated?: () => void;
}

const ServiceEditDialog: React.FC<ServiceEditDialogProps> = ({
  procedure,
  open,
  onClose,
  onServiceUpdated
}) => {
  const {
    formData,
    selectedService,
    selectedRoom,
    error,
    services,
    rooms,
    isEncounterCancelled,
    handleInputChange,
    handleServiceChange,
    handleRoomChange,
    handleSubmit,
    handleDelete,
    handleClose
  } = useServiceForm(procedure, open, onClose, onServiceUpdated);

  if (!procedure) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '2px solid #3b82f6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2.5,
          fontWeight: 600,
          color: '#1e293b'
        }}
      >
        <Box display="flex" alignItems="center">
          <EditIcon sx={{ mr: 1, color: '#3b82f6' }} />
          Edit Service
        </Box>
        <IconButton onClick={handleClose} sx={{ color: '#64748b' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <BasicInfoSection
            priority={formData.priority}
            modifier={formData.modifier}
            isDisabled={isEncounterCancelled}
            onModifierChange={(value) => handleInputChange('modifier', value)}
          />

          <ServiceInfoSection
            selectedService={selectedService}
            serviceCode={formData.serviceCode}
            serviceDescription={formData.serviceDescription}
            description={formData.description}
            services={services}
            isDisabled={isEncounterCancelled}
            onServiceChange={handleServiceChange}
            onDescriptionChange={(value) => handleInputChange('description', value)}
          />

          <TimingInfoSection
            procedureStart={formData.procedureStart}
            procedureDuration={formData.procedureDuration}
            procedureEnd={formData.procedureEnd}
            isDisabled={isEncounterCancelled}
            onStartChange={(value) => handleInputChange('procedureStart', value)}
            onDurationChange={(value) => handleInputChange('procedureDuration', value)}
          />

          <RoomInfoSection
            selectedRoom={selectedRoom}
            rooms={rooms}
            isDisabled={isEncounterCancelled}
            onRoomChange={handleRoomChange}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2.5,
          backgroundColor: '#f8fafc',
          borderTop: '1px solid #e2e8f0'
        }}
      >
        <DialogActionButtons
          isEncounterCancelled={isEncounterCancelled}
          onDelete={handleDelete}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ServiceEditDialog;
